import { ButtonLink, MapLandIcon } from '@landler/component-library';
import { cn } from 'component-library/utils';
import { memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Organization, OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { LandlerLogo } from '@/components/LandlerLogo/LandlerLogo';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useEffectOnce } from '@/hooks/useEffectOnce';
import { useOrganization } from '@/hooks/useOrganization';
import { usePathSet } from '@/hooks/usePathSet';
import { useUIStyle } from '@/hooks/useUIStyle';
import { useUserMe } from '@/hooks/useUserMe';
import { useIsOrganizationMember } from '@/pages/shared/hooks/useIsOrganizationMember';
import { useLimitedAccessOrganization } from '@/pages/shared/hooks/useLimitedAccessOrganization';
import { useNCAParents } from '@/pages/shared/hooks/useNCAParents';
import { useOrganizationId, useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { useUserOrganizationId } from '@/pages/shared/hooks/useUserOrganizationId';
import { assertIsDefined } from '@/utils/assert';
import { buildPath } from '@/utils/buildPath';

import { SidebarToggleButton } from '../../a/Button/SidebarToggleButton';
import { Sidebar, SidebarItem } from './Sidebar';

type NavigationSidebarItem = {
  key: string;
  icon: string | ReactNode;
  label: string;
  to?: string;
  children?: SidebarItem[];
  isText?: boolean;
  isActive?: boolean;
};

type NavigationSidebarProps = {
  isCollapsed: boolean;
  onMinimize: () => void;
};

export const NavigationSidebar = memo(({ isCollapsed, onMinimize }: NavigationSidebarProps) => {
  return <DefaultLandlerSidebar {...{ isCollapsed, onMinimize }} />;
});
NavigationSidebar.displayName = 'NavigationSidebar';

const DefaultLandlerSidebar = ({ isCollapsed, onMinimize }: NavigationSidebarProps) => {
  const navigationItems: NavigationSidebarItem[] = useDefaultNavigationItems();
  const props = getNavigationItems({ navigationItems, isCollapsed, onMinimize });
  return <Sidebar {...props} />;
};

const getNavigationItems = ({
  navigationItems,
  isCollapsed = false,
  onMinimize,
}: {
  navigationItems: NavigationSidebarItem[];
  isCollapsed: boolean;
  onMinimize: () => void;
}) => {
  return {
    header: {
      content: <NavigationSidebarHeader isCollapsed={isCollapsed} onMinimize={onMinimize} />,
      style: 'py-0',
    },
    items: navigationItems.map(({ key, icon, label, to, children, isText = false, isActive = false }) => {
      if (children && children.length > 0) {
        return createSidebarExpandableItem(key, icon, label, children, to, isActive);
      }
      if (isText) {
        return createSideBarTextItem(key, label);
      }

      return createSidebarItem(key, icon, label, to ?? '', isActive);
    }),
    isCollapsed,
  };
};

export const NavigationSidebarHeader = ({ isCollapsed = false, onMinimize }: NavigationSidebarProps) => {
  return (
    <div
      className={cn(
        'flex h-[88px] items-center justify-between border-b border-neutral-black-12 px-6',
        isCollapsed && 'justify-center',
      )}
    >
      <LandlerLogo className={cn(isCollapsed ? 'h-9' : 'h-6')} isCollapsed={isCollapsed} />
      {!isCollapsed && onMinimize && <SidebarToggleButton direction='left' onClick={onMinimize} />}
    </div>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const useDefaultNavigationItems = () => {
  const { t } = useTranslation();
  const user = useUserMe().data;
  const pathSet = usePathSet();
  const hasConservationUiStyle = useUIStyle(OrganizationUIStyle.CONSERVATION);

  const { pathname, search } = useLocation();
  const currentPath = pathname + search;

  const organizationId = useOrganizationId();
  const setOrganizationId = useSetOrganizationId();
  assertIsDefined(organizationId, 'organizationId');
  const organization = useOrganization({ id: organizationId }).data.results[0];

  const isOrganizationMember = useIsOrganizationMember();
  const plotGroups = useOrganizationPlotGroups(organizationId).data;

  const limitedAccessOrganizations = useLimitedAccessOrganization(organizationId, { user_id: user.id }).data;
  const visiblePlotGroups =
    limitedAccessOrganizations && limitedAccessOrganizations.length > 0
      ? limitedAccessOrganizations.map((lao) => lao.plot_group)
      : plotGroups;

  const userOrganizationId = useUserOrganizationId();
  const ncaParentOrganizations = useNCAParents(userOrganizationId).data;

  // Set organization to first NCA parent if current org has 0 plots and NCA parents exist
  const orgHasNoPlots = organization && organization.plots_count === 0;
  const orgHasNCAParents =
    ncaParentOrganizations.length > 0 &&
    ncaParentOrganizations[0]?.id &&
    organizationId !== ncaParentOrganizations[0].id;
  useEffectOnce(() => {
    if (orgHasNoPlots && orgHasNCAParents && ncaParentOrganizations[0]?.id) {
      setOrganizationId(ncaParentOrganizations[0]?.id);
    }
  }, [organization, ncaParentOrganizations, organizationId, setOrganizationId]);

  const getOrganizationsSidebarItems = (organizations: Organization[]): SidebarItem[] => {
    const plotsBasePath = pathSet.dashboard;
    return [
      ...organizations.map((org) =>
        createSidebarItem(
          `org${org.id}`,
          'ri-hotel-line',
          org.name ?? '',
          `${plotsBasePath}?organizationId=${org.id}`,
          currentPath === `${plotsBasePath}?organizationId=${org.id}`,
          () => setOrganizationId(org.id),
        ),
      ),
    ];
  };

  const asInvestorSidebarItems =
    ncaParentOrganizations.length > 0
      ? [
          createSideBarTextItem('asInvestor', t('navbar.organizations.asInvestor')),
          ...getOrganizationsSidebarItems(ncaParentOrganizations),
        ]
      : [];

  const byPermissionSidebarItems = [
    createSideBarTextItem('byPermission', t('navbar.organizations.byPermission')),
    ...getOrganizationsSidebarItems([user.membership.organization ?? organization]),
  ];

  const plotGroupsSidebarItems =
    visiblePlotGroups.length > 0 && isOrganizationMember && !hasConservationUiStyle
      ? [createSidebarItem('groups', '', t('navbar.groups'), pathSet.groups, currentPath === pathSet.groups)]
      : [];

  const homePageChildren = hasConservationUiStyle
    ? undefined
    : [
        createSidebarItem('plots', '', t('navbar.plots'), pathSet.plots, currentPath === pathSet.plots),
        ...plotGroupsSidebarItems,
        createSidebarItem(
          'compare',
          '',
          t('navbar.comparisons'),
          pathSet.comparisons,
          currentPath === pathSet.comparisons,
        ),
      ];

  const [searchParams] = useSearchParams();
  const plotGroupQueryParams = useMemo(() => {
    const queryParamPlotGroupId = searchParams.get('plotGroupId');
    return queryParamPlotGroupId ? { queryParams: { plotGroupId: queryParamPlotGroupId } } : undefined;
  }, [searchParams]);

  const guidanceSection = hasConservationUiStyle
    ? []
    : [
        { key: 'guidance', label: t('navbar.guidance'), isText: true, icon: '', to: '', isActive: false },
        {
          key: 'goals',
          icon: 'ri-compass-line',
          label: t('navbar.goals'),
          to: pathSet.goals,
          isActive: currentPath === pathSet.goals,
        },
        {
          key: 'copilot',
          icon: 'ri-record-circle-line',
          label: t('global.copilot.title'),
          to: pathSet.copilot,
          isActive: currentPath === pathSet.copilot,
        },
        {
          key: 'knowledge',
          icon: 'ri-book-2-line',
          label: t('navbar.knowledge'),
          to: pathSet.knowledge,
          isActive: currentPath === pathSet.knowledge,
        },
      ];

  const portfolioSectionChildren = hasConservationUiStyle
    ? [
        createSidebarItem(
          'natureCapital',
          '',
          t('navbar.natureCapital'),
          pathSet.portfolioNaturalCapital,
          currentPath === pathSet.portfolioNaturalCapital,
        ),
        createSidebarItem(
          'stocks',
          '',
          t('navbar.stocks'),
          pathSet.portfolioStocks,
          currentPath === pathSet.portfolioStocks,
        ),
        createSidebarItem(
          'contracts',
          '',
          t('navbar.contracts'),
          pathSet.portfolioContracts,
          currentPath === pathSet.portfolioContracts,
        ),
      ]
    : [
        createSidebarItem(
          'natureCapital',
          '',
          t('navbar.natureCapital'),
          pathSet.portfolioNaturalCapital,
          currentPath === pathSet.portfolioNaturalCapital,
        ),
        createSidebarItem(
          'forecasts',
          '',
          t('navbar.forecasts'),
          pathSet.portfolioForecasts,
          currentPath === pathSet.portfolioForecasts,
        ),
        createSidebarItem(
          'investments',
          '',
          t('navbar.investments'),
          pathSet.portfolioInvestments,
          currentPath === pathSet.portfolioInvestments,
        ),
        createSidebarItem(
          'units',
          '',
          t('navbar.units'),
          pathSet.portfolioUnits,
          currentPath === pathSet.portfolioUnits,
        ),
        createSidebarItem(
          'stocks',
          '',
          t('navbar.stocks'),
          pathSet.portfolioStocks,
          currentPath === pathSet.portfolioStocks,
        ),
        createSidebarItem(
          'contracts',
          '',
          t('navbar.contracts'),
          pathSet.portfolioContracts,
          currentPath === pathSet.portfolioContracts,
        ),
      ];

  return [
    {
      key: 'activeOrganization',
      label: organization?.name ?? t('global.organization.organizations'),
      icon: 'ri-hotel-line',
      to: '',
      isActive: false,
      children: [...asInvestorSidebarItems, ...byPermissionSidebarItems],
    },
    { key: 'dashboard', label: t('navbar.dashboard'), isText: true, icon: '', to: '', isActive: false },
    {
      key: 'home',
      icon: 'ri-home-5-line',
      label: t('navbar.home'),
      to: pathSet.dashboard,
      isActive: hasConservationUiStyle && currentPath === pathSet.dashboard,
      children: homePageChildren,
    },
    ...(!hasConservationUiStyle
      ? [
          {
            key: 'land',
            icon: <MapLandIcon className='w-6 h-6' />,
            label: t('navbar.land'),
            children: [
              createSidebarItem(
                'carbon',
                '',
                t('navbar.carbon'),
                buildPath(pathSet.carbonDetails, plotGroupQueryParams),
                currentPath === buildPath(pathSet.carbonDetails, plotGroupQueryParams),
              ),
              createSidebarItem(
                'water',
                '',
                t('navbar.water'),
                buildPath(pathSet.waterDetails, plotGroupQueryParams),
                currentPath === buildPath(pathSet.waterDetails, plotGroupQueryParams),
              ),
              createSidebarItem(
                'biodiversity',
                '',
                t('navbar.biodiversity'),
                buildPath(pathSet.biodiversityDetails, plotGroupQueryParams),
                currentPath === buildPath(pathSet.biodiversityDetails, plotGroupQueryParams),
              ),
              createSidebarItem(
                'soil',
                '',
                t('navbar.soil'),
                buildPath(pathSet.soilDetails, plotGroupQueryParams),
                currentPath === buildPath(pathSet.soilDetails, plotGroupQueryParams),
              ),
              createSidebarItem(
                'social',
                '',
                t('navbar.social'),
                buildPath(pathSet.socialDetails, plotGroupQueryParams),
                currentPath === buildPath(pathSet.socialDetails, plotGroupQueryParams),
              ),
            ],
          },
        ]
      : []),
    {
      key: 'portfolio',
      icon: 'ri-line-chart-line',
      label: t('navbar.portfolio'),
      to: undefined,
      children: [...portfolioSectionChildren],
    },
    {
      key: 'reports',
      icon: 'ri-file-text-line',
      label: t('navbar.reports'),
      to: pathSet.reports,
      isActive: currentPath === pathSet.reports,
    },
    ...guidanceSection,
    // TODO: MVP-CW bring these pages to the menu or completely remove them and their routes + components
    // { key: 'contracts', icon: 'ri-file-3-line', label: t('navbar.contracts'), to: pathSet.contracts, isActive: currentPath === pathSet.contracts },
    { key: 'settings', label: t('navbar.settings'), isText: true, icon: '', to: '', isActive: false },
    // TODO: Uncomment when indicator library is fixed
    /* {
     *   key: 'indicator-library',
     *   icon: 'ri-leaf-line',
     *   label: t('navbar.indicators'),
     *   to: pathSet.indicators,
     *   isActive: currentPath === pathSet.indicators,
     * }, */
    ...(hasConservationUiStyle
      ? [
          {
            key: 'mediaUpload',
            icon: 'ri-folder-add-line',
            label: t('navbar.mediaUpload'),
            to: pathSet.mediaUpload,
            isActive: currentPath === pathSet.mediaUpload,
          },
        ]
      : []),
    {
      key: 'profile',
      icon: 'ri-user-line',
      label: t('navbar.profile'),
      to: pathSet.account,
      isActive: currentPath === pathSet.account,
    },
  ];
};

const createSidebarExpandableItem = (
  key: string,
  icon: string | ReactNode,
  label: string,
  children: SidebarItem[],
  to?: string,
  isActive?: boolean,
): SidebarItem => ({
  key,
  content: to ? (
    <ButtonLink
      to={to}
      variant='text'
      className={cn(
        'text-text-primary justify-start grow rounded-md hover:rounded-md text-left leading-[130%] hover:bg-transparent',
        !icon && 'text-text-secondary',
      )}
    >
      {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
      <span>{label}</span>
    </ButtonLink>
  ) : (
    <div
      className={cn('flex items-center gap-2 w-full rounded-md text-left leading-[130%] px-5 py-3 typography-button1')}
    >
      {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
      <span>{label}</span>
    </div>
  ),
  children,
  isActive,
});

const createSidebarItem = (
  key: string,
  icon: string | ReactNode,
  label: string,
  to: string,
  isActive: boolean,
  onClick?: () => void,
): SidebarItem => ({
  key,
  content: (
    <ButtonLink
      variant='text'
      to={to}
      className={cn(
        'text-text-primary justify-start w-full rounded-md hover:rounded-md text-left leading-[130%]',
        !icon && 'text-text-secondary',
        isActive && 'bg-neutral-hover text-text-primary',
      )}
      onClick={onClick}
    >
      {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
      <span>{label}</span>
    </ButtonLink>
  ),
});

const createSideBarTextItem = (key: string, label: string): SidebarItem => ({
  key,
  content: <span className='typography-overline uppercase px-5 pt-4'>{label}</span>,
});
